import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, TemplateRef, ViewChild, inject, output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';

import { Subject, combineLatest, map } from 'rxjs';

import { FormComponent } from '@camelot/form-basic';
import { CamAbstractInput } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { FilterHelper, Menu, MenuComponent, MenuIcon, NavigationComponent } from '@camelot/menu';
import { ENotificationCode, LAZY_SERVICE_TOKEN } from '@camelot/notification';
import { TranslatePipe } from '@camelot/translation';
import {
  ButtonComponent,
  EmptyComponent,
  ErrorComponent,
  LayoutFullPanelComponent,
  LayoutSideComponent,
  LayoutSideContentComponent,
  LayoutSideCtaComponent,
  ListContainerComponent,
  ListElementComponent,
  ListTagComponent,
  ListTitleComponent,
  LoaderComponent,
  TextComponent,
  UserLogoComponent,
} from '@camelot/ui';
import { StopPropagationDirective, TypedTemplateDirective, removeObjectKeys } from '@camelot/utils';

import { getTypeTranslationKey } from '../../helpers/type';
import { FilterModelSource } from '../../models/types';
import { View, ViewType, Views } from '../../services/dto/view';
import { CamGridFormService } from '../../services/grid-form.service';
import { CamGridViewService } from '../../services/grid-view.service';
import { CamTranslationGrid } from '../../translation.service';
import { AbstractGridComponent } from '../abstract-grid.component';

@Component({
  selector: 'cam-views-selection',
  templateUrl: './views-selection.component.html',
  styleUrl: './views-selection.component.scss',
  imports: [
    CommonModule,
    MatMenuModule,
    FormComponent,
    FontIconComponent,
    TranslatePipe,
    LayoutFullPanelComponent,
    LayoutSideComponent,
    LayoutSideContentComponent,
    LayoutSideCtaComponent,
    ListContainerComponent,
    ListElementComponent,
    ListTitleComponent,
    ListTagComponent,
    ErrorComponent,
    LoaderComponent,
    UserLogoComponent,
    TextComponent,
    EmptyComponent,
    ButtonComponent,
    TypedTemplateDirective,
    MenuComponent,
    NavigationComponent,
    StopPropagationDirective,
  ],
})
export class ViewsSelectionComponent extends AbstractGridComponent<any> implements OnInit {
  OSselected = output();

  @ViewChild('choicesTemplate', { read: TemplateRef })
  choicesTemplate!: TemplateRef<void>;

  readonly askValidation$ = new Subject<null>();
  readonly typeToken!: { view: View };
  readonly getTypeTranslationKey = getTypeTranslationKey;

  get viewSelectedId() {
    return this._grid.filters.viewSelected;
  }

  private _editedView: View | null = null;

  private readonly _viewService = inject(CamGridViewService);
  private readonly _formService = inject(CamGridFormService<any>);
  private readonly _notificationService = inject(LAZY_SERVICE_TOKEN);

  constructor() {
    super();
    CamTranslationGrid.getInstance();
  }

  public showForm: 'view' | 'add' | 'edit' | 'copy' = 'view';
  public form: CamAbstractInput[] = this._formService.getViewForm(null, this.choicesTemplate);

  public filterHelper = new FilterHelper([
    { defaultOpen: true, label: this.getTypeTranslationKey(ViewType.private) },
    { defaultOpen: false, label: this.getTypeTranslationKey(ViewType.shared) },
    { defaultOpen: false, label: this.getTypeTranslationKey(ViewType.public) },
  ]);
  public menu: Menu = this.filterHelper.getMenu();

  readonly list$ = combineLatest([this._viewService.views.get$(), this.filterHelper.refresh$]).pipe(
    map(([views, filter]) => {
      switch (filter) {
        case ViewType[ViewType.private]:
          return views?.privateViewSettings;
        case ViewType[ViewType.shared]:
          return views?.sharedViewSettings;
        case ViewType[ViewType.public]:
          return views?.publicViewSettings;
        default:
          return views?.privateViewSettings;
      }
    })
  );

  get grid() {
    return this._grid;
  }

  override ngOnInit() {
    super.ngOnInit();

    this._fetch();

    this._registerSubscription(
      this.grid.ready$.subscribe({
        next: () => {
          const list = this._viewService.views.get();
          if (list) {
            this._defaultSelected(list);
          }
        },
      })
    );
  }

  public selected(view: View) {
    this.grid.filters.applyFilterModel(view, FilterModelSource.view, {
      selected: true,
      viewName: view.id,
    });

    this.OSselected.emit();
  }
  public new() {
    this._editedView = null;
    this.form = this._formService.getViewForm(null, this.choicesTemplate);
    this.showForm = 'add';
  }

  public backToList() {
    this.showForm = 'view';
  }

  public formTitleKey(): string {
    switch (this.showForm) {
      case 'copy':
        return 'grid.viewsselection.more.copy';
      case 'edit':
        return 'grid.viewsselection.more.edit';
      default:
        return 'grid.viewsselection.new';
    }
  }

  public validation() {
    this.askValidation$.next(null);
  }

  public edit(view: View) {
    this._editedView = view;
    this.form = this._formService.getViewForm(view, this.choicesTemplate);
    this.showForm = 'edit';
  }

  public copy(view: View) {
    this._editedView = null;
    this.form = this._formService.getViewForm({ ...view, ...{ name: view.name + '(1)' } }, this.choicesTemplate);
    this.showForm = 'copy';
  }

  public save(data: any) {
    const { filterModel, selectedFields, fieldOptions } = this.grid.filters.extractCurrentFilterModel();
    const view = this._formService.formatView(
      data,
      this.gridId(),
      filterModel ?? '',
      selectedFields ?? [],
      fieldOptions ?? []
    );
    this.requestState.asked();
    const request$ =
      this.showForm === 'edit' && this._editedView
        ? this._viewService.updateView$(this.gridId(), { id: this._editedView.id, name: view.name, type: view.type })
        : this._viewService.addView$(this.gridId(), removeObjectKeys(view, ['id']));
    this._registerSubscription(
      request$.subscribe({
        complete: () => {
          this.requestState.completed();
          this._notificationService.addNotification('notification.common.success', ENotificationCode.success);
          this.backToList();
        },
        error: () => {
          this.requestState.completed();
        },
      })
    );
  }

  public update(view: View) {
    const { filterModel, selectedFields, fieldOptions } = this.grid.filters.extractCurrentFilterModel();
    this.requestState.asked();
    this._registerSubscription(
      this._viewService
        .updateView$(this.gridId(), {
          fieldOptions,
          filterModel,
          id: view.id,
          selectedFields,
        })
        .subscribe({
          complete: () => {
            this.requestState.completed();
            this._notificationService.addNotification('notification.common.success', ENotificationCode.success);
          },
          error: () => {
            this.requestState.completed();
          },
        })
    );
  }

  public getMoreMenu(view: View): Menu<MenuIcon> {
    return new Menu({
      direction: 'vertical',
      elements: [
        new MenuIcon({
          clickManager: { callback: () => this.edit(view) },
          icon: 'modify',
          key: 'edit',
          label: 'grid.viewsselection.more.edit',
          order: 1,
          style: 'dark',
        }),
        new MenuIcon({
          clickManager: { callback: () => this.update(view) },
          icon: 'modify',
          key: 'update',
          label: 'grid.viewsselection.more.update',
          order: 2,
          style: 'dark',
        }),
        new MenuIcon({
          clickManager: { callback: () => this.copy(view) },
          icon: 'copy',
          key: 'copy',
          label: 'grid.viewsselection.more.copy',
          order: 3,
          style: 'dark',
        }),
        new MenuIcon({
          clickManager: { callback: () => this.delete(view) },
          icon: 'delete',
          key: 'delete',
          label: 'grid.viewsselection.more.delete',
          order: 4,
          style: 'dark',
        }),
      ],
    });
  }

  public delete(view: View) {
    this.requestState.asked();
    this._registerSubscription(
      this._viewService.deleteView$(this.gridId(), view).subscribe({
        complete: () => {
          this.requestState.completed();
          this._notificationService.addNotification('notification.common.success', ENotificationCode.success);
        },
        error: () => {
          this.requestState.completed();
        },
      })
    );
  }

  private _fetch() {
    this.requestState.asked();
    this._registerSubscription(
      this._viewService.getViews$(this.gridId()).subscribe({
        complete: () => this.requestState.completed(),
        error: (error: HttpErrorResponse) => {
          this.requestState.onError(error.status, error.statusText);
        },
        next: list => {
          this._defaultSelected(list);
        },
      })
    );
  }

  private _defaultSelected(list: Views) {
    const selected = [...list.privateViewSettings, ...list.sharedViewSettings, ...list.publicViewSettings].find(
      item => item.id === this.grid.filters.viewSelected
    );

    if (selected) {
      this.grid.filters.applyFilterModel(selected, FilterModelSource.view, {
        selected: true,
        viewName: selected.id,
      });
    }
  }
}
